/**
 * StripeCheckoutModal — polished multi-currency (USD / EUR) Stripe Checkout
 * launcher for institutional buyers. Pairs alongside the M-Pesa Paybill rail
 * that the existing AdvancedUpgradeCard flow uses.
 *
 * Behaviour:
 *   1. Pulls package metadata + per-currency price grid from
 *      `GET /api/stripe/packages` on mount.
 *   2. User picks a currency (USD default, EUR available for European buyers).
 *   3. `POST /api/stripe/checkout` returns a hosted Stripe URL — we redirect.
 *      Stripe returns the user to `/app/subscription?stripe_session_id=…` and
 *      the existing polling flow (see Subscription.jsx) confirms activation.
 *
 * Non-goals: NEVER accept an amount from the user — the backend picks it.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Loader2, Globe, ShieldCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { t } from "@/lib/i18n-toasts";
import { useBecoCopy } from "@/lib/beco-i18n";

// Currency picker persistence — European buyers who chose EUR once should
// land on EUR every subsequent time they open the modal.
const CURRENCY_LS_KEY = "md_preferred_currency";

const CURRENCY_META = {
    usd: { symbol: "$",  label: "USD · US Dollar",             flag: "🇺🇸" },
    eur: { symbol: "€",  label: "EUR · Euro (SEPA)",           flag: "🇪🇺" },
};

// Directive 4 · localised subscription-terms block. Rendered above the CTA
// so users must scroll past it before starting checkout. Copy is a
// user-friendly restatement of the Terms of Service software-fee clause.
const SUBSCRIPTION_TERMS = {
    EN: (amount) => `**Subscription Terms:** By subscribing, you agree to a recurring monthly software fee of ${amount} for premium access to Melian Dialogue's educational simulator and specialized behavioral AI features. This recurring fee is solely for software access and learning tools; it is not a financial deposit, and it cannot be used as capital for real investments or trading instruments. You can cancel your subscription at any time via your account dashboard.`,
    SW: (amount) => `**Masharti ya Usajili:** Kwa kujisajili, unakubali ada ya kila mwezi ya ${amount} kwa upatikanaji wa juu wa simulator ya kielimu ya Melian Dialogue na vipengele maalum vya AI. Ada hii ni ya upatikanaji wa programu tu; sio akiba, wala haiwezi kutumika kama mtaji kwa uwekezaji halisi. Unaweza kughairi usajili wakati wowote kupitia dashibodi yako.`,
    SH: (amount) => `**Subscription Terms:** Kwa ku-subscribe, unakubali monthly software fee ya ${amount} kwa premium access ya Melian Dialogue's educational simulator na behavioral AI features. Hii fee ni ya software access tu; sio deposit, wala haiwezi kutumika kama capital ya real investments. Unaweza kucancel wakati wowote kwenye dashboard.`,
    FR: (amount) => `**Conditions d'abonnement :** En vous abonnant, vous acceptez des frais logiciels mensuels récurrents de ${amount} pour l'accès premium au simulateur éducatif de Melian Dialogue et à ses fonctionnalités d'IA comportementale. Ces frais concernent uniquement l'accès au logiciel ; il ne s'agit pas d'un dépôt financier et ils ne peuvent pas servir de capital pour des investissements réels. Vous pouvez annuler à tout moment depuis votre tableau de bord.`,
    AR: (amount) => `**شروط الاشتراك:** بالاشتراك، فإنك توافق على رسوم برمجية شهرية متكررة قدرها ${amount} للوصول المميز إلى محاكي Melian Dialogue التعليمي وميزات الذكاء الاصطناعي السلوكي. هذه الرسوم لأغراض الوصول إلى البرنامج فقط؛ ليست وديعة مالية ولا يمكن استخدامها كرأس مال لاستثمارات حقيقية. يمكنك إلغاء اشتراكك في أي وقت من لوحة حسابك.`,
    PT: (amount) => `**Termos da Assinatura:** Ao assinar, você concorda com uma taxa mensal recorrente de software de ${amount} para acesso premium ao simulador educacional Melian Dialogue e recursos especializados de IA comportamental. Esta taxa é exclusivamente para acesso ao software; não é um depósito financeiro nem pode ser usada como capital para investimentos reais. Você pode cancelar a qualquer momento no painel da sua conta.`,
};

function localisedTerms(lang, amount) {
    const code = (lang || "EN").toString().toUpperCase();
    const fn = SUBSCRIPTION_TERMS[code] || SUBSCRIPTION_TERMS.EN;
    return fn(amount);
}

function formatPrice(amount, ccy) {
    if (typeof amount !== "number") return "—";
    const meta = CURRENCY_META[ccy] || { symbol: ccy.toUpperCase() + " " };
    // Use compact intl formatting when we can; fall back to symbol prefix.
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency", currency: ccy.toUpperCase(), maximumFractionDigits: 0,
        }).format(amount);
    } catch {
        return `${meta.symbol}${amount.toFixed(0)}`;
    }
}

export default function StripeCheckoutModal({ open, onClose, packageId = "advanced_monthly", tierLabel }) {
    const { user } = useAuth();
    const lang = user?.preferred_language;
    const { t: becoT } = useBecoCopy();
    // Directive 4 Amendment · trial-end date shown inside the Obondo terms block.
    // Uses `now + 30d` as a locale-formatted string; the exact backend timestamp
    // is written by `activate_obondo_trial` on Stripe success.
    const trialEndsDisplay = new Date(Date.now() + 30 * 86400 * 1000)
        .toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    const [loading, setLoading] = useState(false);
    const [starting, setStarting] = useState(false);
    const [agreedTerms, setAgreedTerms] = useState(false);
    const [pkgs, setPkgs] = useState(null);
    // Currency defaults from localStorage → user preference is remembered
    // across sessions. Falls back to USD when unset or invalid.
    const [currency, setCurrency] = useState(() => {
        try {
            const saved = localStorage.getItem(CURRENCY_LS_KEY);
            if (saved === "usd" || saved === "eur") return saved;
        } catch { /* ignore */ }
        return "usd";
    });
    // Persist currency changes without waiting for checkout.
    function setCurrencyPersistent(c) {
        setCurrency(c);
        try { localStorage.setItem(CURRENCY_LS_KEY, c); } catch { /* ignore */ }
    }
    // Cross-rates from live market tickers so European buyers see today's
    // fair KES equivalent (Kenya is our home market) next to the EUR / USD
    // sticker price. Falls back silently to defaults if the tickers fail.
    const [rates, setRates] = useState({ usdkes: 129.5, eurkes: 141.2 });

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        Promise.all([
            api.get("/stripe/packages"),
            api.get("/market/ticker/USDKES").catch(() => null),
            api.get("/market/ticker/EURKES").catch(() => null),
        ])
            .then(([pkgRes, u, e]) => {
                setPkgs(pkgRes.data);
                setRates({
                    usdkes: u?.data?.price || 129.5,
                    eurkes: e?.data?.price || 141.2,
                });
            })
            .catch(() => toast.error(t("pricing_load_failed", lang)))
            .finally(() => setLoading(false));
    }, [open, lang]);

    if (!open) return null;

    const pkg = pkgs?.packages?.find((p) => p.id === packageId);
    const supported = pkgs?.currencies || ["usd"];
    const amount = pkg?.amounts?.[currency];
    const kesRate = currency === "eur" ? rates.eurkes : rates.usdkes;
    const kesEquivalent = amount != null ? Math.round(amount * kesRate) : null;

    async function startCheckout() {
        if (!pkg) return;
        setStarting(true);
        try {
            const { data } = await api.post("/stripe/checkout", {
                package_id: packageId,
                currency,
                origin_url: window.location.origin,
            });
            if (data?.checkout_url) {
                // Full redirect to Stripe hosted checkout. Consume the intent
                // flag now so we don't re-open the modal after the return.
                try { localStorage.removeItem("md_stripe_intent"); } catch { /* ignore */ }
                window.location.href = data.checkout_url;
                return;
            }
            toast.error(t("payment_url_missing", lang));
        } catch (err) {
            const msg = err?.response?.data?.detail || t("payment_failed", lang);
            toast.error(msg);
        } finally {
            setStarting(false);
        }
    }

    return (
        <div
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4"
            data-testid="stripe-checkout-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="stripe-modal-title"
            onClick={onClose}
        >
            <div
                className="bg-card border border-border rounded-2xl max-w-lg w-full p-4 sm:p-5 shadow-2xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="overline text-primary tracking-widest">Stripe Checkout · Global card / SEPA</div>
                        <h2 id="stripe-modal-title" className="text-xl font-black tracking-tight mt-1">
                            {tierLabel || pkg?.label || "Institutional monthly"}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close checkout modal"
                        data-testid="stripe-checkout-modal-close"
                        className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {loading && (
                    <div className="flex items-center justify-center py-10 text-muted-foreground">
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        Loading pricing…
                    </div>
                )}

                {!loading && pkg && (
                    <>
                        {/* Currency selector */}
                        <div className="mb-4">
                            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                                <Globe className="w-3.5 h-3.5" /> Pay in
                            </div>
                            <div
                                className="grid grid-cols-2 gap-2"
                                role="radiogroup"
                                aria-label="Currency"
                            >
                                {supported.map((c) => {
                                    const meta = CURRENCY_META[c] || { label: c.toUpperCase(), flag: "" };
                                    const price = pkg.amounts[c];
                                    const active = currency === c;
                                    return (
                                        <button
                                            key={c}
                                            type="button"
                                            role="radio"
                                            aria-checked={active}
                                            onClick={() => setCurrencyPersistent(c)}
                                            data-testid={`stripe-checkout-currency-${c}`}
                                            className={`rounded-lg border p-3 text-left transition-all ${
                                                active
                                                    ? "border-primary bg-primary/10 ring-2 ring-primary/40"
                                                    : "border-border hover:border-primary/50"
                                            }`}
                                        >
                                            <div className="flex items-center gap-2 text-xs font-semibold">
                                                <span className="text-lg leading-none">{meta.flag}</span>
                                                <span className="text-muted-foreground uppercase tracking-wide">{c}</span>
                                            </div>
                                            <div className="mt-1 text-lg font-black mono proportional-nums">
                                                {formatPrice(price, c)}
                                            </div>
                                            <div className="text-[10px] text-muted-foreground">
                                                {meta.label}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Amount summary */}
                        <div className="rounded-lg border border-border bg-muted/40 p-3 mb-4 flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Total today</span>
                            <div className="text-right">
                                <div className="font-black mono proportional-nums text-lg" data-testid="stripe-checkout-total">
                                    {formatPrice(amount, currency)}
                                </div>
                                {kesEquivalent != null && (
                                    <div
                                        className="text-[10px] text-muted-foreground mono proportional-nums leading-tight"
                                        data-testid="stripe-checkout-kes-equivalent"
                                    >
                                        ≈ KES {kesEquivalent.toLocaleString()} · today's rate
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Directive 4 Amendment · Obondo 7-day free-trial terms
                            (shown above the mandatory subscription-terms block
                            when the package grants the 'advanced' tier). */}
                        {pkg?.grants_tier === "advanced" && (
                            <div
                                data-testid="stripe-checkout-obondo-trial-terms"
                                className="rounded-lg border border-sky-500/40 bg-sky-500/10 p-3 mb-3 text-[11px] leading-relaxed"
                                dir={becoT ? undefined : undefined}
                            >
                                <div className="text-xs font-black tracking-tight text-sky-500 mb-1.5">
                                    {becoT ? becoT("trial.terms.title", "Obondo 7-Day Free Trial Terms") : "Obondo 7-Day Free Trial Terms"}
                                </div>
                                <p className="text-muted-foreground">
                                    {(becoT ? becoT("trial.terms.body", "") : "")
                                        .replace("{fee}", formatPrice(amount, currency) + "/mo")
                                        .replace("{ends}", trialEndsDisplay)}
                                </p>
                            </div>
                        )}

                        {/* Directive 4 · Mandatory subscription-terms display */}
                        <div
                            data-testid="stripe-checkout-terms-block"
                            className="rounded-lg border border-border bg-muted/30 p-3 mb-3 text-[11px] leading-relaxed text-muted-foreground"
                        >
                            {localisedTerms(lang, formatPrice(amount, currency) + "/mo")}
                        </div>
                        <label
                            htmlFor="stripe-checkout-agree"
                            className="flex items-start gap-2 text-xs text-foreground mb-4 cursor-pointer select-none"
                        >
                            <input
                                id="stripe-checkout-agree"
                                type="checkbox"
                                checked={agreedTerms}
                                onChange={(e) => setAgreedTerms(e.target.checked)}
                                data-testid="stripe-checkout-terms-agree"
                                className="mt-0.5 accent-primary"
                            />
                            <span>
                                I have read and agree to the subscription terms above.
                            </span>
                        </label>

                        <Button
                            className="w-full"
                            disabled={starting || amount == null || !agreedTerms}
                            onClick={startCheckout}
                            data-testid="stripe-checkout-submit-btn"
                        >
                            {starting ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening Stripe…</>
                            ) : (
                                <><CreditCard className="w-4 h-4 mr-2" /> Continue to Stripe</>
                            )}
                        </Button>

                        <div className="mt-3 text-[11px] text-muted-foreground flex items-start gap-1.5">
                            <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5 text-primary/70" />
                            <span>
                                You'll be redirected to Stripe's hosted checkout. Card / SEPA never touches our servers.
                                Cancel anytime — no auto-renewal on the M-Pesa Paybill rail.
                            </span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
